/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {getSearchTitle} from '@ion/applications/src/globalsearch/GlobalSearchTitle';
import {translate} from '@ion/core/src/locale/locale';
import {JobSearchFieldLabels, JobSearchFields} from '@ion/core/src/types/entities';

export enum FilterLogicalOrFields {
  jobId = 'jobId',
  progressStatus = 'progressStatus',
  username = 'username'
}

enum Operators {
  AND = 'AND',
  OR = 'OR'
}

export type Filters = {
  disableDelete?: boolean,
  keyword: string,
  field: string
}

/**
 * Parses the filter string and formats it into a usable filter object array.
 *
 * @param str       - The filter string.
 * @param activeTab - The active tab identifier.
 */
export const getFilters = (str, activeTab): Filters => {
  const parts = str.split(` ${Operators.AND} `).map(part => part.trim());
  return parts.map((part: string) => {
    part = part.slice(1, -1);
    const [field, keyword] = part.split(':');
    if (activeTab === 'my-jobs' && field === 'username') {
      return {
        disableDelete : true,
        keyword       : keyword?.replace(/[()]/g, ''),
        field
      };
    }
    return {
      keyword : formatFilterValue(field, field === 'username' ? keyword?.replace(/[()]/g, '') : keyword),
      field
    };
  });
};

const formatFilterValue = (key: string, keyword: string) => {
  if (FilterLogicalOrFields[key] && keyword.includes(Operators.OR)) {
    return keyword.replaceAll(` ${Operators.OR} `, ',');
  }
  return keyword;
};

/**
 * Retrieves the default filters based on the active tab and current filters.
 *
 * @param tabs      - The array of tab objects.
 * @param activeTab - The active tab identifier.
 */
export const getDefaultFilters = (tabs, activeTab, isOnlyAutoJobs) => {
  const currentTabFilter = tabs.find(tab => tab.key === activeTab)?.filter;
  const currentJobNameFilter = tabs.find(tab => tab.key === activeTab)?.nameFilter;
  const fileFilter = tabs.find(tab => tab.key === activeTab)?.fileName;
  const allJobsFilters   = activeTab === 'jobs' && currentTabFilter === '(jobId:*)';

  if (!allJobsFilters) {
    const allFilters = getFilters(currentTabFilter, activeTab) || [];
    if (currentJobNameFilter) {
      allFilters.push({field: 'name', keyword: currentJobNameFilter, disableDelete: isOnlyAutoJobs});
    }
    if (fileFilter) {
      allFilters.push({field: 'filename', keyword: fileFilter});
    }
    return allFilters;
  }

  const filters = [];

  if (currentJobNameFilter) {
    filters.push({field: 'name', keyword: currentJobNameFilter, disableDelete: isOnlyAutoJobs});
  }
  if (fileFilter) {
    filters.push({field: 'filename', keyword: fileFilter});
  }
  
  return filters;
};

/**
 * Generates search options based on input value.
 *
 * @param val       - The input search value.
 * @param activeTab - The active tab identifier.
 */
// eslint-disable-next-line max-params
export const createSearchOptions = (val, activeTab, disableJobIdOption, isOnlyAutoJobs) => {
  const searchOptions = [];
  val = removeModule(val);
  let searchableFields = Object.values(JobSearchFields);
  if (activeTab === 'my-jobs') {
    searchableFields = searchableFields.filter(item => item !== 'username');
  }
  if (isOnlyAutoJobs) {
    searchableFields = searchableFields.filter(item => item !== 'name');
  }
  searchableFields.forEach(field => {
    const title = getSearchTitle(val);
    searchOptions.push({
      item       : field,
      text       : val,
      isDisabled : field === 'jobId' ? disableJobIdOption : false,
      name       : `${title} in ${JobSearchFieldLabels[field]}`
    });
  });
  return searchOptions;
};

/**
 * Removes module part from the search string.
 *
 * @param str - The input search string.
 */
export const removeModule = (val: string, target = ' in '): string => (
  val?.includes(target) ? val.slice(0, val.lastIndexOf(target)).trim() : val
);

export const getDeleteMessage = (selectedItem, taskIndex: number = 0) => {
  if (selectedItem && !Array.isArray(selectedItem)) {
    const {name, tasks, device, identifier} = selectedItem || {};
    const taskName = tasks?.[taskIndex]?.task?.metaData?.title;
    return translate('jobsearchengine:delete.jobDeleteMessage', {
      label : name || taskName || device?.esn || identifier || ''
    });
  }
};

/**
 * Returns the specified field's value from the tabs array based on the provided active tab.
 * 
 * ```typescript
 *
 *   import {getFilterByField} from '@ion/ion-ctc-admin/src/routes/provisioning/jobsearchengine/JobSearchUtils';
 *
 *   const filter = getFilterByField(tabs, activeTab, 'nameFilter');
 * 
 * ```
 * 
 * @param tabs      - The tabs array.
 * @param activeTab - The active tab identifier.
 * @param field     - the field whose value needs to be returned.
 */
export const getFilterByField = (
  tabs, activeTab: string, field: string): string | Filters[] => (
  tabs?.find(tab => tab.key === activeTab)?.[field]
);
